import { Button, Tooltip, theme } from 'antd';
import type { ButtonProps } from 'antd';
import {
  ChartNoAxesColumnIncreasing as BarChartOutlined,
  CalendarDays as CalendarOutlined,
  Trash2 as DeleteOutlined,
  Pencil as EditOutlined,
  Eye as EyeOutlined,
  EllipsisVertical as MoreOutlined,
  UsersRound as TeamOutlined,
  UserRoundMinus as UserDeleteOutlined,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { forwardRef } from 'react';

export type TableActionType =
  | 'edit'
  | 'delete'
  | 'view'
  | 'assign'
  | 'remove'
  | 'calendar'
  | 'more'
  | 'statistics';

interface TableActionIconProps extends Omit<ButtonProps, 'type' | 'icon' | 'title'> {
  action: TableActionType;
  title?: string;
  onClick?: ButtonProps['onClick'];
  disabled?: boolean;
  tooltip?: boolean;
}

const getIconByAction = (action: TableActionType): ReactNode => {
  switch (action) {
    case 'edit':
      return <EditOutlined />;
    case 'delete':
      return <DeleteOutlined />;
    case 'view':
      return <EyeOutlined />;
    case 'assign':
      return <TeamOutlined />;
    case 'remove':
      return <UserDeleteOutlined />;
    case 'calendar':
      return <CalendarOutlined />;
    case 'more':
      return <MoreOutlined />;
    case 'statistics':
      return <BarChartOutlined />;
    default:
      return <EditOutlined />;
  }
};

const getColorByAction = (action: TableActionType, token: ReturnType<typeof theme.useToken>['token']) => {
  switch (action) {
    case 'delete':
      return token.colorError;
    case 'remove':
      return token.colorWarning;
    case 'edit':
    case 'view':
    case 'assign':
    case 'calendar':
    case 'more':
    case 'statistics':
    default:
      return token.colorTextSecondary;
  }
};

export const TableActionIcon = forwardRef<HTMLButtonElement, TableActionIconProps>(function TableActionIcon(
  { action, title, onClick, disabled, tooltip = true, ...buttonProps },
  ref,
) {
  const { token } = theme.useToken();

  const button = (
    <Button
      ref={ref}
      type="text"
      size="small"
      className="ys-table-action-btn"
      data-action={action}
      icon={getIconByAction(action)}
      aria-label={title || action}
      title={title}
      onClick={onClick}
      disabled={disabled}
      style={{ color: getColorByAction(action, token) }}
      {...buttonProps}
    />
  );

  if (!title || !tooltip) {
    return button;
  }

  return <Tooltip title={title}>{button}</Tooltip>;
});

export default TableActionIcon;
